import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterHeader from "../../components/FilterHeader";
import styles from "./Odds.module.scss";
import {
  fractionalOddsValuesMap,
  DEBOUNCE_TIMER,
  decimalOddsValuesMap,
  AnalyticsTypeMap,
  LOCATION_MAP,
  decimalValueToFractionalLabelMap,
} from "../../../../constants";
import { useDebounce } from "../../../../hooks/useDebounce.js";
import useBetSlip from "../../../../hooks/useBetSlip.js";
import { sendAnalyticsEvent } from "../../../../lib/postMessage.js";
import { selectOddsAndUpdateFilter } from "../../../../actions/filters.js";
import { getMapPosition } from "../../../../lib/index.js";
import { convertPositionToDecimal } from "../../../../lib/odds.js";
import { FILTERS, SELECT } from "../../../../constants/analytics.js";

const GREEN = "#009745";

const min = 0;
const max = decimalOddsValuesMap.size - 1;

const Odds = () => {
  const dispatch = useDispatch();
  const {
    filters: { odds: filterOdds },
    prefetchedFilters: { odds: prefetchedFilterOdds },
  } = useSelector((state) => state.filter);
  const {
    minOdds = null,
    maxOdds = Infinity,
    isDecimal = false,
  } = prefetchedFilterOdds;
  // check if the odds are a non floating point number this indicates its positional value and needs to be converted to a decimal value
  const isPositionalOdds =
    Number.isInteger(minOdds) && Number.isInteger(maxOdds);
  const convertedOdds =
    !isDecimal && isPositionalOdds
      ? {
          minOdds: convertPositionToDecimal(minOdds),
          maxOdds: convertPositionToDecimal(maxOdds),
        }
      : { minOdds, maxOdds };

  const { activeFilterView, activeView } = useSelector((state) => state.view);
  const [, , oddsFormat] = useBetSlip();
  const [minVal, setMinVal] = useState(
    getMapPosition(decimalValueToFractionalLabelMap, convertedOdds.minOdds) ||
      min
  );
  const [maxVal, setMaxVal] = useState(
    getMapPosition(decimalValueToFractionalLabelMap, convertedOdds.maxOdds) ||
      max
  );
  const minValLabel =
    oddsFormat === "Fractional"
      ? fractionalOddsValuesMap.get(minVal ?? min)
      : decimalOddsValuesMap.get(minVal ?? min).toLocaleString("en", {
          minimumFractionDigits: 1,
        });
  const maxValLabel =
    oddsFormat === "Fractional"
      ? fractionalOddsValuesMap.get(maxVal ?? max)
      : decimalOddsValuesMap.get(maxVal ?? max).toLocaleString("en", {
          minimumFractionDigits: 1,
        });
  const sliderTrackRef = useRef(null);
  const minDisplayRef = useRef(null);
  const maxDisplayRef = useRef(null);
  const minMaxDisplayRef = useRef(null);
  const debouncedMinVal = useDebounce(minVal, DEBOUNCE_TIMER);
  const debouncedMaxVal = useDebounce(maxVal, DEBOUNCE_TIMER);
  const [hasAttemptedSelection, setHasAttemptedSelection] = useState(false);
  const [displaysOverlapping, setDisplaysOverlapping] = useState(false);

  const updateOddsFilter = useCallback(() => {
    // we want to store the odds in a decimal format to store in the redux store
    const convertedMinVal = convertPositionToDecimal(debouncedMinVal);
    const convertedMaxVal = convertPositionToDecimal(debouncedMaxVal);
    sendAnalyticsEvent({
      event: FILTERS,
      type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
      action: SELECT,
      minValue: fractionalOddsValuesMap.get(debouncedMinVal),
      maxValue: fractionalOddsValuesMap.get(debouncedMaxVal),
      location: LOCATION_MAP.get(activeView) || "",
    });
    dispatch(
      selectOddsAndUpdateFilter({
        minOdds: convertedMinVal,
        maxOdds: convertedMaxVal,
        isDecimal: true,
      })
    );
  }, [
    debouncedMinVal,
    debouncedMaxVal,
    dispatch,
    activeFilterView,
    activeView,
  ]);

  useEffect(() => {
    const percent1 = (minVal / max) * 100;
    const percent2 = (maxVal / max) * 100;

    sliderTrackRef.current.style.background = `linear-gradient(to right, #dadae5 ${percent1}% , ${GREEN} ${percent1}% , ${GREEN} ${percent2}%, #dadae5 ${percent2}%)`;
    if (!displaysOverlapping) {
      minDisplayRef.current.style.left = `${percent1}%`;
      maxDisplayRef.current.style.left = `${percent2}%`;
    } else {
      minMaxDisplayRef.current.style.left = `${(percent1 + percent2) / 2}%`;
    }
  }, [minVal, maxVal, displaysOverlapping]);

  // logic below checks if the device is
  // beneath a certain viewport width and
  // if the two percentages overlap
  // when compared to the offset value
  // if so we set a state of true which
  // controll a css class
  // this is to mitigate the displays
  // overlapping with eachother
  useEffect(() => {
    const percent1 = (minVal / max) * 100;
    const percent2 = (maxVal / max) * 100;
    let willDisplaysOverlap = false;

    const offset = Math.floor(percent2) - Math.floor(percent1);

    if (typeof window !== "undefined") {
      // if window width is less the 400, the odds displays will overlap
      // when they are 3 selections apart
      // 16 is the max offset for 3 selections
      if (window.innerWidth < 400 && offset <= 16) {
        willDisplaysOverlap = true;
      }

      // if window width is less the 630, the odds displays will overlap
      // when they are 2 selections apart
      // 11 is the max offset for 2 selections
      if (window.innerWidth < 630 && offset <= 11) {
        willDisplaysOverlap = true;
      }

      // if window width is less than 1055, the odds displays will overlap
      // when they are 1 selections apart
      // 6 is the max offset for 1 selection
      if (window.innerWidth <= 1055 && offset <= 6) {
        willDisplaysOverlap = true;
      }
    }

    setDisplaysOverlapping(willDisplaysOverlap);
  }, [minVal, maxVal]);

  useEffect(() => {
    hasAttemptedSelection && updateOddsFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMinVal, debouncedMaxVal, updateOddsFilter]);

  // the useEffects below handles cancelling the filter options
  // in order to reset them back to starting position
  // if prefetchedFilterOdds has no minOdds and maxOdds it means the user has cleared the filter
  useEffect(() => {
    if (
      prefetchedFilterOdds.minOdds === null &&
      prefetchedFilterOdds.maxOdds === null
    ) {
      setMinVal(min);
      setMaxVal(max);
      setHasAttemptedSelection(false);
    }
  }, [filterOdds, prefetchedFilterOdds]);

  const setValue = (value, type) => {
    const hasReset =
      type === "min"
        ? value === min && maxVal === max
        : value === max && minVal === min;

    if (hasReset) {
      setHasAttemptedSelection(false);
      sendAnalyticsEvent({
        event: FILTERS,
        type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
        action: SELECT,
        minValue: fractionalOddsValuesMap.get(min),
        maxValue: fractionalOddsValuesMap.get(max),
        location: LOCATION_MAP.get(activeView) || "",
      });
      dispatch({
        type: "REMOVE_FROM_ODDS_SELECTIONS",
        payload: {
          filter: activeFilterView,
        },
      });
    } else {
      setHasAttemptedSelection(true);
    }
    // set the min and max values to the decimal value
    type === "min" ? setMinVal(value) : setMaxVal(value);
  };

  return (
    <div>
      <FilterHeader />

      <div className={styles.container}>
        <div ref={sliderTrackRef} className={styles.sliderTrack}></div>
        <div className={styles.displayContainer}>
          {displaysOverlapping ? (
            <span
              ref={minMaxDisplayRef}
              className={`${styles.display} ${styles.displaysOverlapping}`}
            >
              {`${minValLabel} - ${maxValLabel}`}
            </span>
          ) : (
            <>
              <span ref={minDisplayRef} className={styles.display}>
                {minValLabel}
              </span>
              <span ref={maxDisplayRef} className={styles.display}>
                {maxValLabel}
              </span>
            </>
          )}
        </div>
        <div className={styles.rangeContainer}>
          <input
            className={`${styles.range} ${styles.leftSlider}`}
            type="range"
            min={min}
            max={max}
            value={minVal}
            onChange={(event) => {
              const value = Math.min(
                Number(event.target.value),
                getMapPosition(decimalOddsValuesMap, maxVal) - 1
              );
              setValue(value, "min");
            }}
          />
        </div>
        <div className={styles.rangeContainer}>
          <input
            className={`${styles.range} ${styles.rightSlider}`}
            type="range"
            min={min}
            max={max}
            value={maxVal}
            onChange={(event) => {
              const value = Math.max(
                Number(event.target.value),
                getMapPosition(decimalOddsValuesMap, minVal) + 1
              );
              setValue(value, "max");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Odds;
